import type { LabeledValuePayload } from '@Application/Users/UserStatuses/Actions/userStatusesFetched';

/**
 * The failing or blocked step of the latest transcription run of an interview. `message` and
 * `isConfigurationFailure` are decided by the server; `canRetry` gates the retry gesture.
 */
export type TranscriptionIssuePayload = {
	readonly run: { readonly id: number };
	readonly stepRun: { readonly id: number };
	readonly step: LabeledValuePayload;
	readonly isStalled: boolean;
	readonly message: string;
	readonly isConfigurationFailure: boolean;
	readonly canRetry: boolean;
};

/**
 * One input of a run (an interview, a dictated note, a document of the dossier). Titles are never
 * carried: the front resolves them from the other stores by id. `declaredDocumentType` is set on a
 * document carried automatically or chosen for a type. A source whose interview and document were
 * both deleted carries `null` for both; so does an imported document deleted (all three are `null`).
 */
export type WorkflowRunSourcePayload = {
	readonly id: number;
	readonly category: LabeledValuePayload;
	readonly interview: { readonly id: number } | null;
	readonly document: { readonly id: number } | null;
	readonly importedDocument: { readonly id: number } | null;
	readonly declaredDocumentType: { readonly id: number } | null;
	readonly addedAt: string;
	readonly state: LabeledValuePayload;
	readonly stateReason: string | null;
	readonly canTranscribe: boolean;
	readonly transcriptionIssue: TranscriptionIssuePayload | null;
};

export type WorkflowRunMissingSourcePayload = {
	readonly documentType: { readonly id: number };
	readonly verifiedOnly: boolean;
};

/**
 * One document of the run's group. `step` is the definition, `stepRun` the execution (`null` before
 * launch): retrying a document targets `stepRun`. `canRetry` is decided by the server alone.
 */
export type WorkflowRunDocumentPayload = {
	readonly stage: number;
	readonly step: { readonly id: number };
	readonly stepRun: { readonly id: number } | null;
	readonly documentType: { readonly id: number };
	readonly document: { readonly id: number } | null;
	readonly state: LabeledValuePayload;
	readonly canRetry: boolean;
	readonly failureMessage: string | null;
};

/**
 * The wire shape of a run, shared by every route answering one: `GET`, open, add a source, launch the
 * generation or a transcription, retry a step, and the event stream. `state` is `open`, `in_progress` or
 * `completed`; `startedAt` is meaningless while `state.value === 'open'`. `canGenerate`, `blockedReason`
 * and `canAbandon` are decided by the server alone.
 */
export type WorkflowRunPayload = {
	readonly id: number;
	readonly beneficiary: { readonly id: number } | null;
	readonly workflowKey: string;
	readonly version: number;
	readonly label: string;
	readonly kind: LabeledValuePayload;
	readonly state: LabeledValuePayload;
	readonly openedAt: string;
	readonly startedAt: string;
	readonly completedAt: string | null;
	/** The generation is running (persisted status `running`, a blocked step included); a run in progress that only waits for review is not generating. */
	readonly isGenerating: boolean;
	readonly handledAt: string | null;
	readonly canMarkHandled: boolean;
	readonly canGenerate: boolean;
	readonly blockedReason: string | null;
	readonly canAbandon: boolean;
	readonly acceptsInterviews: boolean;
	readonly acceptsNotes: boolean;
	readonly acceptsDocuments: boolean;
	readonly sources: readonly WorkflowRunSourcePayload[];
	readonly missingAutomaticSources: readonly WorkflowRunMissingSourcePayload[];
	readonly documents: readonly WorkflowRunDocumentPayload[];
};

export type WorkflowRunFetchedAction = {
	type: 'WORKFLOW_RUN_FETCHED';
	payload: WorkflowRunPayload;
	facilityId: number;
	typeKey: string;
	subjectId: number;
};

export default (
	payload: WorkflowRunPayload,
	facilityId: number,
	typeKey: string,
	subjectId: number,
): WorkflowRunFetchedAction => ({
	type: 'WORKFLOW_RUN_FETCHED',
	payload,
	facilityId,
	typeKey,
	subjectId,
});
